import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation, NetworkStatus } from '@apollo/client';
import PropTypes from 'prop-types';

import moment from 'moment';
import { Box } from '@mui/material';
import { MdEdit } from 'react-icons/md';
import { IoTrashSharp } from 'react-icons/io5';
import { ToastContainer, toast } from 'react-toastify';
import Modal from '@mui/material/Modal';
import DialogContent from '@mui/material/DialogContent';
import { useSelector } from 'react-redux';

import RulesConfigGrid from './Grid/Grid';
import Search from './Search/SearchRC';
import Filter from './Filter/FilterRC';
import ModalDetail from '../../../../ModalDetail/ModalDetail';
import * as Q from '../../../../../constants/graphqlQueries';

import './SearchTab.css';
import RuleDetails from './RuleDetails/RuleDetails';
import UserRoles from '../../../../../constants/userRoles';
import transactionResponse from '../../../../../constants/transactionResponse';
import ActiveSwitch from './Switch/ActiveSwitch';

const transactionMessages = {
  moduleName: 'Rule',
};
const ruleType = {
  enterprise: 'Enterprise',
  custom: 'Custom'
}
const tabConfiguration = { 
  ruleTypes: [
    {id: 1, name: 'Enterprise'},
    {id: 2, name: 'Custom'}
  ], 
  alertTypes: [
    {id: 1, name: 'Reminder'},
    {id: 2, name: 'Performance'},
    {id: 3, name: 'Schedule'}
  ] 
}

const SearchTab = ({ handleEditRule, ruleResponse }) => {
  const toastId = useRef();
  const operationType = useRef();
  const [timerRefetchData, setTimerRefetchData] = useState(null);
  const user = useSelector((state) => state.User);
  // const [tabConfiguration, setTabConfiguration] = useState(null);
  const [data, setData] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [gridTitle] = useState('Rules');
  const [filterAlert, setFilterAlert] = useState(null);
  const [filterRule, setFilterRule] = useState(null);
  const [filterActive, setFilterActive] = useState(null);
  const [filterRuleName, setFilterRuleName] = useState('');
  const [clearFilters, setClearFilters] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [ruleToDestroy, setRuleToDestroy] = useState();
  
  // get data
  const [getRuleData,
    {
      data: rulesConfigurationData,
      loading: isLoading,
      refetch: refetchRulesData,
      networkStatus
    }] = useLazyQuery(Q.FIND__GRID__DATA, { notifyOnNetworkStatusChange: true });
  const [destroyRule, { data: deleteReply } ] = useMutation(Q.DELETE__RULE);
  const [activateOrPauseRule, { data: ruleReply } ] = useMutation(Q.ACTIVATE__PAUSE__RULE);
  
  const handleRuleToDestroy = (rule) => {
    setRuleToDestroy(rule);
    setOpen(true);
  }
  const handleActiveSwitch = (checked, RuleId, toggleIsDisabled) => {
    clearTimeout(timerRefetchData);
    toggleIsDisabled();
    activateOrPauseRule({ variables: { Id: RuleId, Play: checked, UpdateBy: user.EmailAddress } });
  }

  // Client-side filtering for Rule Name and Rule Active
  const applyClientFilters = (sourceData, ruleNameFilter, activeFilterValue) => {
    let filtered = [...sourceData];

    // Filter by Rule Name (case-insensitive partial match)
    if (ruleNameFilter && ruleNameFilter.trim() !== '') {
      const search = ruleNameFilter.toLowerCase().trim();
      filtered = filtered.filter(rule =>
        rule.RuleName && rule.RuleName.toLowerCase().includes(search)
      );
    }

    // Filter by Active status
    if (activeFilterValue === 'active') {
      filtered = filtered.filter(rule => rule.IsActive === 1);
    } else if (activeFilterValue === 'inactive') {
      filtered = filtered.filter(rule => rule.IsActive !== 1);
    }
    // 'all' or null = no filter

    return filtered;
  };

  const gridConfigurations = {
    keyGrid: 1,
    columns: [
      { title: 'Rule', field: 'RuleType.Description', width: '50px' },
      { title: 'Rule Name', field: 'RuleName', width: '200px' },
      { title: 'Tagging', field: 'TagScheduleSegmentCode', width: '50px', render: rowData => rowData.TagScheduleSegmentCode ? 'Yes' : 'No' },
      { title: 'Flagging', field: 'FlagFrequency', width: '50px', render: rowData => rowData.FlagFrequency ? 'Yes' : 'No' },
      { title: 'Alert', field: 'AlertType.Description', width: '50px' },
      { title: 'Alarm', field: 'ScheduleAlarmCode', width: '50px' },
      { title: 'Client', field: 'Client.DescriptionShort', width: '250px', render: rowData => rowData.Client === null ? 'All' : `${rowData.Client.DescriptionShort} (${rowData.Client.ClientId})` },
      { title: 'Program', field: 'Program.DescriptionShort', width: '250px', render: rowData => rowData.Program === null ? 'All' : `${rowData.Program.DescriptionShort} (${rowData.Program.ProgramId})` },
      { title: 'Project', field: 'Project.DescriptionShort', width: '250px', render: rowData => rowData.Project === null ? 'All' : `${rowData.Project.DescriptionShort} (${rowData.Project.ProjectId})` },
      { title: 'Site', field: 'Site.DescriptionShort', width: '250px', render: rowData => rowData.Site === null ? 'All' : rowData.Site.DescriptionShort },
      { title: 'Create By', field: 'CreateBy', width: '180px' },
      { title: 'Modified By', field: 'UpdateBy', width: '180px' },
      { title: 'Modified Time', field: 'UpdateDate', width: '180px', render: (rowData) => 
        moment.utc(rowData.UpdateDate).local().format('M/D/YYYY HH:mm'),
      },
    ],
    options: {
      defaultExpanded: true,
      filtering: false,
      actionsColumnIndex: 0,
      tableLayout: 'auto',
      actionsCellStyle: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
      },
    },
    localization: {
      header: {
        actions: 'Actions',
      },
      body: {
        emptyDataSourceMessage: 'Filter the rules you would like to see',
      },
    },
    actions: [
      (rowData) => (
        {
          disabled:  user.Role === UserRoles.WFM && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner &&
            (rowData.RuleType?.Description === ruleType.enterprise && !(user.Claims.RulesConfigurationEnterpriseEdit) ||
            rowData.RuleType?.Description === ruleType.custom && !(user.Claims.RulesConfigurationCustomEdit)),
          icon: () => (
            <MdEdit style={{
              height: 20, width: 20, position: 'relative', marginTop: '0.4rem', padding: '0.1rem',
            }}
            />
          ),
          tooltip: 'Edit',
          // eslint-disable-next-line no-shadow
          onClick: (e, rowData) => handleEditRule(rowData.RuleId),
      }),
      (rowData) => (
        {
          disabled: user.Role === UserRoles.WFM && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner &&
            (rowData.RuleType?.Description === ruleType.enterprise && !(user.Claims.RulesConfigurationEnterpriseDelete) ||
            rowData.RuleType?.Description === ruleType.custom && !(user.Claims.RulesConfigurationCustomDelete)),
          icon: () => (
            <IoTrashSharp style={{
              height: 20, width: 20, position: 'relative', marginTop: '0.4rem', padding: '0.1rem',
            }}
            />
          ),
          tooltip: 'Delete',
          // eslint-disable-next-line no-shadow
          onClick: (e, rowData) => handleRuleToDestroy(rowData),
      }),
      rowData => (
        {
          disabled: user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner &&
          (rowData.RuleType?.Description === ruleType.enterprise && !(user.Claims.RulesConfigurationEnterpriseActive) ||
          rowData.RuleType?.Description === ruleType.custom && !(user.Claims.RulesConfigurationCustomActive)),
          icon: () => {
            const switchStatus = rowData.IsActive === 1;
            const { RuleId, DeactivationReason } = rowData;
            return <ActiveSwitch 
                      stopped={!DeactivationReason}
                      switchStatus={switchStatus} 
                      referenceValue={RuleId}
                      handleActiveSwitch={handleActiveSwitch} 
                    />
            }
        }
      )
    ],
    detailPanel: rowData => <RuleDetails data={rowData} />
  };

  const [gridConfiguration] = useState(gridConfigurations);

  const modalOptions = {
    title: 'Confirm Action',
    description: `Do you want to delete ${ruleToDestroy && ruleToDestroy.RuleName}? This action is permanent`,
  };
  const onFilterClick = (filterOption) => {
    setClearFilters(false);
    if (filterOption.type === 'rule') {
      setFilterRule(filterOption.id);
      if (filterOption.id === 1 || !filterOption.id) {
        setIsDisabled(true);
      }
      if (filterOption.id === 2) {
        setIsDisabled(false);
      } 
    }
    if (filterOption.type === 'alert') {
      setFilterAlert(filterOption.id);
    }
  };
  const onSearchClick = (client, program, project, site, ruleName) => {
    setFilterRuleName(ruleName || '');
    getRuleData({
      variables: {
        RuleTypeId: filterRule,
        AlertTypeId: filterAlert,
        ClientCode: client ? client.ClientId : null,
        ProgramCode: program ? program.ProgramId : null,
        ProjectCode: project ? project.ProjectId : null,
        SiteCode: site ? site.SiteId : null,
      },
    });
  };
  const onActiveFilterChange = (activeValue) => {
    setFilterActive(activeValue);
  };
  const onClearSearch = () => {
    setData([]);
    setUnfilteredData([]);
    setFilterRule(null);
    setFilterAlert(null);
    setFilterActive(null);
    setFilterRuleName('');
    setClearFilters(true);
    setIsDisabled(true);
  };
  const handleDestroyRule = async () => {
    setOpen(false);
    await destroyRule({ variables: { Id: ruleToDestroy.RuleId } });
    await refetchRulesData({
      variables: {
        RuleTypeId: filterRule,
        AlertTypeId: filterAlert,
      }
    });
  };
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (deleteReply) {
      toast.dismiss(toastId.current);
      toastId.current = toast(transactionResponse(transactionMessages.moduleName, deleteReply.deleteRule.status));
    }
  }, [deleteReply]);
  useEffect(() => {
    if (rulesConfigurationData) {
      if (networkStatus === NetworkStatus.ready) {
        // Workaround to rename Adherence to Reminder
        const renameAdherence = rulesConfigurationData.findGridData.map(rule => {
          if (rule.AlertType?.Description === 'Adherence') {
            return { 
              ...rule,
              AlertType: { Description: 'Reminder' }
            }
          }
          return rule;
        })
        setUnfilteredData(renameAdherence);
        setData(applyClientFilters(renameAdherence, filterRuleName, filterActive));

        if (operationType.current === NetworkStatus.refetch) {
          toastId.current = toast(transactionResponse(transactionMessages.moduleName, ruleReply.activateOrPauseRule.status));
          operationType.current = null;
        }
      }
    }
  }, [networkStatus]);
  // Re-apply client-side filters when Rule Active filter changes
  useEffect(() => {
    if (unfilteredData.length > 0) {
      setData(applyClientFilters(unfilteredData, filterRuleName, filterActive));
    }
  }, [filterActive]);
  useEffect(() => {
    if (ruleResponse) {
      toast.dismiss(toastId.current);
      toastId.current = toast(transactionResponse(transactionMessages.moduleName, ruleResponse));
    }
  }, [ruleResponse]);
  useEffect(() => {
    if (ruleReply) {
      const timerId = setTimeout( () => refetchRulesData({
          variables: {
            RuleTypeId: filterRule,
            AlertTypeId: filterAlert,
          }
        }), 500);

      toast.dismiss(toastId.current);
      operationType.current = NetworkStatus.refetch;
      setTimerRefetchData(timerId);
    }
  }, [ruleReply]);

  return (
    <Box className='__search'>
      <div className='__search__filters'>
        <Filter
          tabs={tabConfiguration}
          onFilterClick={onFilterClick}
          clearFilters={clearFilters}
        />
        <Search
          onSearchClick={onSearchClick}
          onClearSearch={onClearSearch}
          isDisabled={isDisabled}
          onActiveFilterChange={onActiveFilterChange}
        />
      </div>
      <div className='__search__grid'>
        <Box className='__search__grid--container'>
          <Box className='__search__grid--container-title'>
            {gridTitle}
          </Box>
        </Box>
        { data
              && (
              <RulesConfigGrid
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...gridConfiguration}
                isLoading={isLoading}
                data={data}
              />
              )}
      </div>
      <Modal
        aria-labelledby='transition-modal-title'
        aria-describedby='transition-modal-description'
        open={open}
        onClose={handleClose}
      >
        <DialogContent className='dialog delete-rule'>
          <ModalDetail className='modal delete-rule'
            modalOptions={modalOptions}>
              <button className='simple-modal-btn' type='button' onClick={handleDestroyRule}>Delete</button>
              <button className='simple-modal-btn' type='button' onClick={handleClose}>Cancel</button>
            </ModalDetail>
        </DialogContent>
      </Modal>
      <ToastContainer hideProgressBar/>
    </Box>
  );
};

SearchTab.propTypes = {
  handleEditRule: PropTypes.func,
  ruleResponse: PropTypes.string
};

SearchTab.defaultProps = {
  handleEditRule: null,
  ruleResponse: null
};

export default SearchTab;
